import { Controller } from '@hotwired/stimulus';
import { buildTabSelector, updateSidebarActiveState } from '../constants/undistractor_tabs';
import { extensionPresent } from './helpers/extension_presence';
import UndistractorOptionStore from '../stores/undistractor_option_store';

// Handles row filtering for undistractor settings tabs.
// URL management is handled by the persistent-tabs controller.

// A ceiling, not a guess at the sync's latency — the store wins this race on every normal load, and
// what it buys is that a sync which never arrives still gets a table rather than a blank column.
const SYNC_WAIT_MS = 1500;

export default class extends Controller {
  static targets = ['filterableRow', 'table'];
  static values = { activeTab: String, awaitExtensionSync: Boolean };

  connect() {
    const tab = this.#getInitialTab();
    if (tab) {
      this.#setTab(tab);
    } else {
      this.#activateFirstTab();
    }
    this.#revealTable();
  }

  disconnect() {
    this.#stopWaitingForSync();
  }

  // Signed out, only the extension knows which undistractors are on, so the rows paint every one off
  // until its sync lands — the presence attribute reveals the page well before that (UNS-4939). Gated on
  // presence: without it no sync is coming that .unscreen-content-hider isn't already waiting on, and
  // the signed-in table renders authoritative server state, so neither ever waits here.
  #revealTable() {
    if (!this.awaitExtensionSyncValue || !extensionPresent()) return this.#showTable();

    const store = UndistractorOptionStore.getInstance();
    if (store.extensionSynced) return this.#showTable();

    // The store rather than the sync event: this controller sits above the one listening for it, so on
    // a dispatch that lands between the two connects the event is already gone by the time we'd hear it.
    this.unsubscribe = store.subscribeAll(() => {
      if (store.extensionSynced) this.#showTable();
    });
    this.syncTimer = setTimeout(() => this.#showTable(), SYNC_WAIT_MS);
  }

  #showTable() {
    this.#stopWaitingForSync();
    this.tableTarget.classList.remove('d-none');
  }

  #stopWaitingForSync() {
    clearTimeout(this.syncTimer);
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  #activateFirstTab() {
    const firstTab = this.element.querySelector('.tab-btn');
    if (firstTab) firstTab.click();
  }

  handleTabClick(event) {
    const tab = event.params.tab;
    this.#filterTabs(tab);
    this.#setActiveTab(event.currentTarget);
    updateSidebarActiveState(tab);
  }

  #setActiveTab(clickedTab) {
    const allTabs = this.element.querySelectorAll('.tab-btn');
    allTabs.forEach((t) => t.classList.remove('active'));
    clickedTab.classList.add('active');
  }

  #filterTabs(tab) {
    this.filterableRowTargets.forEach((row) => {
      const visible = row.dataset.undistractorTabFilterUndistractorParam === tab;
      row.classList.toggle('d-none', !visible);
    });
  }

  #setTab(tab) {
    const tabDom = this.element.querySelector(buildTabSelector(tab));
    tabDom?.click();
  }

  #getInitialTab() {
    return this.activeTabValue || this.#activeTabInDom() || null;
  }

  // persistent-tabs is listed first in data-controller, so by now it has resolved the tab from the URL
  // and marked the button. Without this, a Turbo restore — whose cached snapshot has a blank
  // activeTabValue — would fall through to #activateFirstTab and stomp the URL's tab back to the first.
  #activeTabInDom() {
    return this.element.querySelector('.tab-btn.active')?.dataset.undistractorTabFilterTabParam ?? null;
  }
}
